<template>
  <div class="flex items-center gap-1 p-2 border border-common-border rounded-md">
    <UIButton
      v-tooltip.top="'Add a field'"
      :size
      variant="secondary"
      label="Field"
      :disabled="!isFieldsAvailable"
      @click="handleAddField"
    />
    <UIButton
      v-tooltip.top="'Add a number'"
      :size
      variant="secondary"
      label="Number"
      :disabled="!isFieldsAvailable"
      @click="handleAddNumber"
    />
    <UIButtonIcon
      v-tooltip.top="'Add a plus operator'"
      v-bind="buttonAttrs"
      :leading="PlusIcon"
      :disabled="!isOperatorAvailable"
      @click="handleAddOperatorPlus"
    />
    <UIButtonIcon
      v-tooltip.top="'Add a minus operator'"
      v-bind="buttonAttrs"
      :leading="MinusIcon"
      :disabled="!isOperatorAvailable"
      @click="handleAddOperatorMinus"
    />
    <UIButtonIcon
      v-tooltip.top="'Add a divide operator'"
      v-bind="buttonAttrs"
      :leading="DivideIcon"
      :disabled="!isOperatorAvailable"
      @click="handleAddOperatorDivide"
    />
    <UIButtonIcon
      v-tooltip.top="'Add a multiply operator'"
      v-bind="buttonAttrs"
      :leading="XMarkIcon"
      :disabled="!isOperatorAvailable"
      @click="handleAddOperatorXMark"
    />
    <UIButtonIcon
      v-tooltip.top="'Add a percent operator'"
      v-bind="buttonAttrs"
      :leading="h('span', '%')"
      :disabled="!isOperatorAvailable"
      @click="handleAddOperatorPercent"
    />
    <UIButtonIcon
      v-tooltip.top="'Add a parentheses expression'"
      v-bind="buttonAttrs"
      :leading="h('span', '( )')"
      :disabled="!isExpressionAvailable"
      @click="handleAddParentheses"
    />
    <UIButtonIcon
      v-tooltip.top="'Remove the last expression'"
      :size
      variant="secondary"
      :leading="BackspaceIcon"
      :disabled="!isRemoveAvailable"
      @click="handleRemove"
    />
    <UIButtonIcon
      v-tooltip.top="'Clear all expressions'"
      :size
      variant="secondary"
      :leading="h('span', 'AC')"
      :disabled="!isRemoveAvailable"
      @click="handleClear"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'

import { ButtonFill, ButtonSize, ButtonVariant } from '@types'
import { GridColumn } from '@/components/UI/Grid/utils/types'
import { CalculatedColumn } from './utils/types'
import { CalculatedColumnOperator } from './utils/enums'

import { FIELD_PREFIX, NUMBER_PREFIX } from './utils/const'

import { UIButton, UIButtonIcon } from '@ui/buttons'
import { BackspaceIcon, DivideIcon, MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'

type Props = {
  fields: GridColumn[]
  disabled?: boolean
}

const props = defineProps<Props>()

const modelValue = defineModel<CalculatedColumn['expression']>({ required: true })
const isInParentheses = defineModel<boolean>('is-in-parentheses', { default: false })

const size = 'xs' as ButtonSize

const buttonAttrs = computed(() => {
  return {
    size,
    variant: 'gray' as ButtonVariant,
    fill: 'outlined' as ButtonFill
  }
})

const elements = computed({
  get() {
    if (!modelValue.value) return []
    return modelValue.value.split(' ')
  },
  set(value) {
    modelValue.value = value.join(' ')
  }
})

const lastElement = computed(() => {
  return elements.value.at(-1)
})

const isLastElementIsOperator = computed(() => {
  return Object.values(CalculatedColumnOperator).includes(lastElement.value as CalculatedColumnOperator)
})

const isLastElementIsField = computed(() => {
  return lastElement.value?.startsWith(FIELD_PREFIX) && lastElement.value?.length > 1
})

const isLastElementIsNumber = computed(() => {
  return lastElement.value?.startsWith(NUMBER_PREFIX) && lastElement.value?.length > 1
})

const isLastElementIsParenthesisClose = computed(() => {
  return lastElement.value === ')'
})

const isFieldsAvailable = computed(() => {
  return !props.disabled && (
    isLastElementIsOperator.value || !modelValue.value || 
    (isInParentheses.value && !isLastElementIsField.value && !isLastElementIsNumber.value)
  )
})

const isOperatorAvailable = computed(() => {
  return !props.disabled && (isLastElementIsField.value || isLastElementIsNumber.value || isLastElementIsParenthesisClose.value)
})

const isExpressionAvailable = computed(() => {
  return !props.disabled && !isInParentheses.value && (isLastElementIsOperator.value || !modelValue.value)
})

const isRemoveAvailable = computed(() => {
  return !!modelValue.value
})

const getIndex = () => {
  if (isInParentheses.value && lastElement.value === ')') {
    return elements.value.length - 1
  } else {
    return elements.value.length
  }
}

const addElement = (element: CalculatedColumn['expression']) => {
  const index = getIndex()
  elements.value = [...elements.value.slice(0, index), `${element}`, ...elements.value.slice(index)]
}

const removeElement = (index: number) => {
  elements.value = [...elements.value.slice(0, index - 1), ...elements.value.slice(index)]
}

const handleAddField = () => {
  addElement(FIELD_PREFIX)
}

const handleAddNumber = () => {
  addElement(NUMBER_PREFIX)
}

const handleAddOperator = (operator: CalculatedColumnOperator) => {
  addElement(operator)
}

const handleAddOperatorPlus = () => {
  handleAddOperator(CalculatedColumnOperator.SUM)
}

const handleAddOperatorMinus = () => {
  handleAddOperator(CalculatedColumnOperator.SUBTRACT)
}

const handleAddOperatorDivide = () => {
  handleAddOperator(CalculatedColumnOperator.DIVIDE)
}

const handleAddOperatorXMark = () => {
  handleAddOperator(CalculatedColumnOperator.MULTIPLY)
}

const handleAddOperatorPercent = () => {
  handleAddOperator(CalculatedColumnOperator.PERCENT)
}

const handleAddParentheses = () => {
  elements.value = [...elements.value, '(', ')']
  isInParentheses.value = true
}

const handleRemove = () => {
  const index = getIndex()
  if (lastElement.value === ')' && !isInParentheses.value) {
    isInParentheses.value = true
    return
  } else if (lastElement.value === ')' && elements.value.at(-2) === '(') {
    isInParentheses.value = false
    elements.value = elements.value.slice(0, index - 1)
    return
  }
  removeElement(index)
  setTimeout(() => {
    if (lastElement.value === ')') {
      isInParentheses.value = true
    }
  })
}

const handleClear = () => {
  modelValue.value = ''
  isInParentheses.value = false
}
</script>
